// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';

// internal
import { Team } from '../../../../models/training.models';
import TeamsService from '../../../../services/teams.service';
import useService from '../../../../hooks/service.hook';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import { UserAvatar } from '../../../../components/user-avatar/user-avatar.component';
import AthleteSchedulePanel from '../../../../components/athlete-schedule-panel/athlete-schedule-panel.component';
import { TrainingLogProvider } from '../../../training-log/training-log.context';
import { TRAINING_LOG_NAV_ITEMS } from '../../../training-log/training-log.constants';
import DashboardPage from '../../../training-log/pages/dashboard/dashboard.page';
import LogPage from '../../../training-log/pages/log/log.page';
import RawDataPage from '../../../training-log/pages/raw-data/raw-data.page';
import DataInfoPage from '../../../training-log/pages/data-info/data-info.page';
import CoachAthleteTrainingModal from '../../../../components/coach-athlete-training-modal';
import { palette } from '../../../../theme';
import '../../../../components/athlete-member-bottom-sheet/athlete-member-bottom-sheet.component.css';
import '../../../../components/athlete-member-training-log-bottom-sheet/athlete-member-training-log-bottom-sheet.component.css';
import './athlete-schedules.page.css';

interface AthleteSchedulesPageProps {
  team: Team;
}

interface RosterMember {
  id: string | number;
  name: string;
  role?: string;
  profileImage?: string | null;
  email?: string;
}

type PageSubTab = 'schedule' | 'training-log';

const MEMBER_PARAM = 'member';
const SUB_PARAM = 'sub';
const ATHLETE_LOG_TAB_PARAM = 'athleteLogTab';

const getMemberId = (member: RosterMember): string => String(member.id);

const AthleteSchedulesPage: FC<AthleteSchedulesPageProps> = ({ team }) => {
  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();
  const [searchParams, setSearchParams] = useSearchParams();

  const teamId = String(team.id ?? team.teamId);
  const isCoach = team.role?.toLowerCase() === 'coach';

  const [membersLoading, setMembersLoading] = useState(true);
  const [coaches, setCoaches] = useState<RosterMember[]>([]);
  const [athletes, setAthletes] = useState<RosterMember[]>([]);
  const [retiredAthletes, setRetiredAthletes] = useState<RosterMember[]>([]);
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
  const [selectedTrainingId, setSelectedTrainingId] = useState<string | number | null>(null);

  const selectedMemberId = searchParams.get(MEMBER_PARAM);
  const subTab: PageSubTab = searchParams.get(SUB_PARAM) === 'training-log' ? 'training-log' : 'schedule';
  const activeLogTab = searchParams.get(ATHLETE_LOG_TAB_PARAM) ?? 'dashboard';
  const activeLogTabIndex = Math.max(
    0,
    TRAINING_LOG_NAV_ITEMS.findIndex((item) => item.tab === activeLogTab)
  );

  useEffect(() => {
    if (!teamId || !isCoach) return;

    setMembersLoading(true);
    Promise.all([teamsService.getAllTeamMembers(teamId), teamsService.getRetiredAthletes(teamId)])
      .then(([membersResponse, retiredResponse]) => {
        const activeMembers: RosterMember[] = Array.isArray(membersResponse.data?.data)
          ? membersResponse.data.data
          : [];
        const retired: RosterMember[] = Array.isArray(retiredResponse.data?.data) ? retiredResponse.data.data : [];

        setCoaches(activeMembers.filter((m) => m.role?.toLowerCase() === 'coach'));
        setAthletes(activeMembers.filter((m) => m.role?.toLowerCase() !== 'coach'));
        setRetiredAthletes(retired);
        setMembersLoading(false);
      })
      .catch(() => {
        setMembersLoading(false);
        setCoaches([]);
        setAthletes([]);
        setRetiredAthletes([]);
        showSnackbar('There was an error loading team members.', 'error');
      });
  }, [teamId, isCoach]);

  const filterMembers = (list: RosterMember[]): RosterMember[] => {
    const query = sidebarSearchQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((member) => (member.name ?? '').toLowerCase().includes(query));
  };

  const filteredCoaches = useMemo(() => filterMembers(coaches), [coaches, sidebarSearchQuery]);
  const filteredAthletes = useMemo(() => filterMembers(athletes), [athletes, sidebarSearchQuery]);
  const filteredRetired = useMemo(() => filterMembers(retiredAthletes), [retiredAthletes, sidebarSearchQuery]);

  const allSelectable = useMemo(() => [...filteredCoaches, ...filteredAthletes], [filteredCoaches, filteredAthletes]);

  const selectedMember = useMemo(() => {
    if (!selectedMemberId) return null;
    return [...coaches, ...athletes].find((m) => getMemberId(m) === selectedMemberId) ?? null;
  }, [selectedMemberId, coaches, athletes]);

  useEffect(() => {
    if (membersLoading || allSelectable.length === 0) return;

    const exists = selectedMemberId
      ? allSelectable.some((m) => getMemberId(m) === selectedMemberId) ||
        [...coaches, ...athletes].some((m) => getMemberId(m) === selectedMemberId)
      : false;

    if (!exists) {
      const first = allSelectable[0] ?? coaches[0] ?? athletes[0];
      if (!first) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MEMBER_PARAM, getMemberId(first));
          if (!next.get(SUB_PARAM)) next.set(SUB_PARAM, 'schedule');
          return next;
        },
        { replace: true }
      );
    }
  }, [membersLoading, allSelectable, selectedMemberId, coaches, athletes, setSearchParams]);

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null || value === '') next.delete(key);
        else next.set(key, value);
      });
      return next;
    });
  };

  const handleSelectMember = (member: RosterMember) => {
    updateParams({ [MEMBER_PARAM]: getMemberId(member) });
  };

  const handleSubTabChange = (nextSub: PageSubTab) => {
    updateParams({
      [SUB_PARAM]: nextSub,
      ...(nextSub === 'schedule' ? { [ATHLETE_LOG_TAB_PARAM]: null, period: null, week: null } : {}),
    });
  };

  const handleLogTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    const newTab = TRAINING_LOG_NAV_ITEMS[newIndex]?.tab ?? 'dashboard';
    updateParams({
      [ATHLETE_LOG_TAB_PARAM]: newTab,
      period: null,
      week: null,
    });
  };

  const handleNavigateToTrainingLog = (period: number, week: number) => {
    updateParams({
      [SUB_PARAM]: 'training-log',
      [ATHLETE_LOG_TAB_PARAM]: 'training-log',
      period: String(period),
      week: String(week),
    });
  };

  const displayLogSubpage = () => {
    switch (activeLogTab) {
      case 'training-log':
        return <LogPage />;
      case 'raw-data':
        return <RawDataPage />;
      case 'data-info':
        return <DataInfoPage />;
      case 'dashboard':
      default:
        return <DashboardPage />;
    }
  };

  const renderSidebarSection = (title: string, members: RosterMember[], options?: { disabled?: boolean }) => {
    if (members.length === 0) return null;
    const disabled = Boolean(options?.disabled);
    return (
      <Box className={`as-sidebar-section${disabled ? ' as-sidebar-section--retired' : ''}`}>
        <Typography className="as-sidebar-section-title">{title}</Typography>
        {members.map((member) => {
          const id = getMemberId(member);
          const selected = !disabled && selectedMemberId === id;
          const roleLabel =
            member.role && member.role.length > 0
              ? member.role.charAt(0).toUpperCase() + member.role.slice(1)
              : disabled
              ? 'Retired'
              : 'Athlete';

          return (
            <ListItemButton
              key={id}
              selected={selected}
              disabled={disabled}
              onClick={disabled ? undefined : () => handleSelectMember(member)}
              className="as-sidebar-item"
              sx={(theme) => ({
                alignItems: 'center',
                gap: 1.25,
                py: 1,
                px: 1.5,
                borderLeft: 3,
                borderColor: selected ? 'primary.main' : 'transparent',
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : undefined,
                opacity: disabled ? 0.65 : 1,
                cursor: disabled ? 'default' : 'pointer',
                '&.Mui-disabled': {
                  opacity: 0.65,
                },
              })}
            >
              <UserAvatar src={member.profileImage} name={member.name || ''} size={32} fontSize="0.7rem" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: selected ? 13 : 12,
                    fontWeight: selected ? 600 : 500,
                    color: palette.text.primary,
                    lineHeight: 1.25,
                  }}
                >
                  {member.name || 'Unknown'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: disabled ? palette.grey[500] : palette.text.secondary,
                    lineHeight: 1.2,
                  }}
                >
                  {roleLabel}
                </Typography>
              </Box>
            </ListItemButton>
          );
        })}
      </Box>
    );
  };

  if (!isCoach) {
    return null;
  }

  return (
    <div className="team-details-page athlete-schedules-page">
      <Box className="as-layout">
        <Paper elevation={0} className="as-sidebar">
          <Box className="as-sidebar-search">
            <TextField
              value={sidebarSearchQuery}
              onChange={(e) => setSidebarSearchQuery(e.target.value)}
              placeholder="Search athletes…"
              size="small"
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 12,
                  bgcolor: palette.grey[50],
                  borderRadius: '8px',
                },
              }}
            />
          </Box>

          <Box className="as-sidebar-list">
            {membersLoading ? (
              <Box className="as-sidebar-loading">
                <CircularProgress size={28} />
              </Box>
            ) : (
              <List disablePadding>
                {renderSidebarSection('Coaches', filteredCoaches)}
                {renderSidebarSection('Athletes', filteredAthletes)}
                {renderSidebarSection('Retired', filteredRetired, { disabled: true })}
                {!filteredCoaches.length && !filteredAthletes.length && !filteredRetired.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
                    No members found
                  </Typography>
                ) : null}
              </List>
            )}
          </Box>
        </Paper>

        <Box className="as-main">
          <div className="as-sub-tabs">
            <button
              type="button"
              className={`as-sub-tab${subTab === 'schedule' ? ' as-sub-tab--active' : ''}`}
              onClick={() => handleSubTabChange('schedule')}
            >
              Athlete Schedule
            </button>
            <button
              type="button"
              className={`as-sub-tab${subTab === 'training-log' ? ' as-sub-tab--active' : ''}`}
              onClick={() => handleSubTabChange('training-log')}
            >
              Training Log
            </button>
          </div>

          {!selectedMemberId || membersLoading ? (
            <Box className="as-main-loading">
              <CircularProgress size={40} />
            </Box>
          ) : subTab === 'schedule' ? (
            <AthleteSchedulePanel
              team={team}
              memberId={selectedMemberId}
              memberName={selectedMember?.name}
              memberPreview={{
                name: selectedMember?.name,
                profileImage: selectedMember?.profileImage,
              }}
            />
          ) : (
            <Box className="as-training-log">
              <Box className="as-tl-header">
                <Box className="as-tl-header-left">
                  <UserAvatar
                    src={selectedMember?.profileImage}
                    name={selectedMember?.name || ''}
                    size={36}
                    fontSize="0.85rem"
                  />
                  <Box>
                    <Typography className="as-tl-name">{selectedMember?.name || 'Athlete'}</Typography>
                    {selectedMember?.email ? (
                      <Typography className="as-tl-meta">{selectedMember.email}</Typography>
                    ) : null}
                  </Box>
                </Box>

                <Tabs
                  value={activeLogTabIndex}
                  onChange={handleLogTabChange}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  sx={{
                    minHeight: 36,
                    height: 36,
                    width: 'fit-content',
                    bgcolor: palette.grey[100],
                    borderRadius: '10px',
                    p: '3px',
                    flexShrink: 0,
                    '.MuiTabs-flexContainer': { gap: '2px', height: '100%' },
                    '.MuiTab-root': {
                      minHeight: 0,
                      height: '100%',
                      fontSize: 13,
                      color: palette.text.secondary,
                      fontWeight: 500,
                      padding: '0 14px',
                      textTransform: 'none',
                      borderRadius: '8px',
                    },
                    '.Mui-selected': {
                      color: palette.text.primary,
                      fontWeight: 700,
                      bgcolor: palette.background.paper,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                    },
                  }}
                >
                  {TRAINING_LOG_NAV_ITEMS.map((item) => (
                    <Tab key={item.id} label={item.label} />
                  ))}
                </Tabs>
              </Box>

              <TrainingLogProvider
                key={selectedMemberId}
                coachView={{ teamId, memberId: selectedMemberId }}
                onNavigateToTrainingLog={handleNavigateToTrainingLog}
                onOpenAthleteTraining={setSelectedTrainingId}
              >
                <div
                  className={`training-log-sheet-content as-tl-content${
                    activeLogTab === 'raw-data' ? ' training-log-sheet-content--raw-data' : ''
                  }`}
                >
                  {displayLogSubpage()}
                </div>
              </TrainingLogProvider>

              {selectedMember ? (
                <CoachAthleteTrainingModal
                  open={selectedTrainingId !== null}
                  onClose={() => setSelectedTrainingId(null)}
                  teamId={teamId}
                  memberId={selectedMemberId}
                  trainingId={selectedTrainingId}
                  memberPreview={{
                    name: selectedMember.name || 'Athlete',
                    profileImage: selectedMember.profileImage,
                  }}
                />
              ) : null}
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default AthleteSchedulesPage;
